import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type ChipIconColorKeyConstantType,
    type ChipCustomColorConstantType,
    type ChipStretchConstantType,
    type ChipColorsConstantType,
    type ChipDynamicStyleType,
    type ChipSizeConstantType,
    type ChipMeasures,
    type ChipColors
} from "./type";
import {
    getTypeColorRoles,
    TYPE_COLOR_ROLES,
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle,
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    webStyle
} from "../../utils";

export const getChipSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    iconSizes,
    size
}: ChipSizeConstantType): ChipMeasures => {
    return getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        step: -1,
        size
    });
};

export const getChipIsStretched = ({
    surfaceStyle
}: ChipStretchConstantType): boolean => {
    return surfaceStyle.alignSelf === "stretch" || (typeof surfaceStyle.width === "string" && surfaceStyle.width.endsWith("%"));
};

export const getChipIconColorKey = ({
    isDisabled,
    isSelected,
    variant,
    type
}: ChipIconColorKeyConstantType): keyof NCoreUIKit.IconContentColors => {
    if(isDisabled) {
        return "disabled";
    }

    const currentRole = getTypeColorRoles({
        isSelected,
        variant,
        type
    }).iconColor;

    if(currentRole.group === "content.icon") {
        return currentRole.key;
    }

    const outlineRole = TYPE_COLOR_ROLES[type].outline.iconColor;

    return outlineRole.group === "content.icon" ? outlineRole.key : "default";
};

export const getChipCustomColor = ({
    tokenColors = {},
    customColor,
    colors
}: ChipCustomColorConstantType): string => {
    const _customColor = customColor as keyof NCoreUIKit.ProjectColorPalette;

    return colors.project[_customColor] ?? tokenColors[customColor] ?? customColor;
};

export const getChipColors = ({
    customTitleColor,
    customIconColor,
    customColor,
    isDisabled,
    isSelected,
    variant,
    colors,
    type
}: ChipColorsConstantType): ChipColors => {
    const currentColors: ChipColors = {
        ...getTypeColors({
            variant: isDisabled ? "tonal" : variant,
            isDisabled,
            isSelected,
            colors,
            type
        }),
        iconColorKey: getChipIconColorKey({
            isDisabled,
            isSelected,
            variant,
            type
        })
    };

    if(isDisabled || isSelected) {
        return currentColors;
    }

    if(customColor) {
        currentColors.containerColor = getChipCustomColor({
            tokenColors: colors.content.container,
            customColor,
            colors
        });
    }

    if(customTitleColor) {
        currentColors.titleColor = getChipCustomColor({
            tokenColors: colors.content.text,
            customColor: customTitleColor,
            colors
        });
    }

    if(customIconColor) {
        const _customIconColor = customIconColor as keyof NCoreUIKit.IconContentColors;

        currentColors.iconColor = getChipCustomColor({
            tokenColors: colors.content.icon,
            customColor: customIconColor,
            colors
        });

        if(colors.content.icon[_customIconColor]) {
            currentColors.iconColorKey = _customIconColor;
        }
    }

    return currentColors;
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        maxWidth: "100%",
        flexShrink: 1,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    close: {
        justifyContent: "center",
        alignItems: "flex-end",
        position: "absolute",
        bottom: 0,
        top: 0,
        end: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    checkContainer: {
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        bottom: 0,
        start: 0,
        top: 0,
        end: 0
    },
    checkSlot: {
        justifyContent: "center",
        overflow: "hidden"
    },
    surface: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center",
        maxWidth: "100%"
    },
    content: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1
    },
    closeIndicator: {
        justifyContent: "center",
        alignItems: "center"
    },
    title: {
        alignSelf: "baseline",
        textAlign: "center",
        flexShrink: 1
    },
    anchor: {
        position: "relative",
        maxWidth: "100%"
    },
    root: {
        alignItems: "center",
        maxWidth: "100%",
        flexShrink: 1
    },
    stretchedAnchor: {
        alignSelf: "stretch"
    }
});

export const useStyles = ({
    isCloseFocusVisible,
    spreadBehaviour,
    isFocusVisible,
    currentColors,
    isCloseButton,
    isInteractive,
    shapeConfigs,
    touchTargets,
    currentSize,
    isStretched,
    pointerType,
    isDisabled,
    isIconOnly,
    isLoading,
    userStyle,
    radiuses,
    borders,
    colors,
    spaces,
    type
}: ChipDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    });

    const focusColorKey = getStateColorKey({
        isDisabled,
        type
    });

    const styles = {
        closeIcon: {
            marginEnd: Math.max(borders.line + currentSize.paddingHorizontal - spaces.spacingXs, 0)
        } as Mutable<ViewStyle>,
        closeIndicator: {
            marginEnd: -(spaces.spacingXs + spaces.spacingXxs),
            marginVertical: -spaces.spacingXxs,
            marginStart: -spaces.spacingXxs,
            borderRadius: radiuses.full,
            padding: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        surface: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            minWidth: currentSize.minHeight,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        closePlaceholder: {
            height: currentSize.iconSize,
            width: currentSize.iconSize
        } as Mutable<ViewStyle>,
        container: {
            minHeight: hitArea.minHeight,
            minWidth: hitArea.minWidth
        } as Mutable<ViewStyle>,
        close: {
            width: hitArea.minWidth
        } as Mutable<ViewStyle>,
        content: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        anchor: {
        } as Mutable<ViewStyle>,
        root: {
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: focusColorKey,
        isFocusVisible,
        borders,
        colors
    }));

    Object.assign(styles.closeIndicator, getFocusRingStyle({
        isFocusVisible: isCloseFocusVisible,
        colorKey: focusColorKey,
        isInset: true,
        borders,
        colors
    }));

    if(!isInteractive && !isCloseButton) {
        delete styles.container.minHeight;
        delete styles.container.minWidth;
    }

    if(isIconOnly) {
        delete styles.surface.paddingHorizontal;
    }

    if(isLoading && isInteractive) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(isLoading) {
        Object.assign(styles.close, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(isStretched) {
        Object.assign(styles.anchor, stylesheet.stretchedAnchor);
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(isCloseButton ? styles.root : styles.container, getHugStyle({
            style: userStyle
        }));
    }

    return styles;
};
export default stylesheet;
